<template>
	<div class="d-flex min-vh-100 align-items-center login">
		<div v-if="showLoginForm" class="login-env-warning">
			<div v-if="isWarningOpen" class="login-env-warning__box">
				<div class="login-env-warning__header">
					<strong>⚠️ คำเตือน</strong>
					<button class="login-env-warning__toggle" @click="isWarningOpen = false">✕</button>
				</div>
				<p class="mb-1">ขณะนี้เปิดใช้งาน Login Form อยู่ ซึ่งอาจเป็นความเสี่ยงด้านความปลอดภัย</p>
				<code>VUE_APP_IS_SHOW_LOGIN_FORM_ENABLED=1</code>
				<p class="mb-0 mt-1">กรุณาปิด setting นี้ทันทีที่ใช้งานเสร็จ</p>
			</div>
			<button v-else class="login-env-warning__badge" @click="isWarningOpen = true">
				⚠️
			</button>
		</div>

		<CContainer>
			<CRow>
				<CCol>
					<div class="login-form">
						<div class="logo">
							<FontAwesomeIcon
								class="icon"
								:icon="['fas', 'user']"
							/>
						</div>

						<CForm @submit.prevent="handleLogin">
							<h1 class="login-form-heading">
								{{ $t('page.login.form.title') }}
							</h1>
							<!-- eslint-disable-next-line vue/no-v-html -->
							<p class="login-form-description" v-html="$t('page.login.form.description')"></p>

							<CInput
								v-if="showLoginForm"
								v-model.trim="$v.username.$model"
								:is-valid="isUsernameValid"
								:invalid-feedback="errorUsernameText"
								class="login-form-input"
								placeholder="Username"
								autocomplete="username email"
								data-test-id="input-username"
								@input="clearLoginError"
							>
								<template #prepend-content>
									<CIcon name="cil-user" />
								</template>
							</CInput>
							<CInput
								v-if="showLoginForm"
								v-model.trim="$v.password.$model"
								:is-valid="isPasswordValid"
								:invalid-feedback="errorPasswordText"
								class="login-form-input"
								placeholder="Password"
								type="password"
								autocomplete="curent-password"
								data-test-id="input-password"
								@input="clearLoginError"
							>
								<template #prepend-content>
									<CIcon name="cil-lock-locked" />
								</template>
							</CInput>
							<div v-if="showLoginForm" class="text-center">
								<CButton
									:disabled="isLoading || allowLoginTimer > 0"
									type="submit"
									color="primary"
									class="submit-button px-4"
									data-test-id="button-submit"
								>
									{{ allowLoginTimer > 0
										? allowLoginMessage
										: $t('page.login.form.button.submit')
									}}
								</CButton>
							</div>
						</CForm>

						<SocialGroup />
					</div>
				</CCol>
			</CRow>
		</CContainer>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import SocialGroup from '@/components/social/SocialGroup.vue';
import { required } from 'vuelidate/lib/validators';
import { pathOr } from '../assets/js/helpers';
import errorMessage from '../enums/errorMessage';

export default {
	name: 'LoginPage',
	components: {
		SocialGroup,
	},
	data() {
		return {
			username: null,
			password: null,

			version: process.env.VUE_APP_VERSION,
			allowLoginSeconds: 0,
			allowLoginMessage: '',
			allowLoginTimer: null,
			isWarningOpen: true,
		};
	},
	validations: {
		username: {
			required,
		},
		password: {
			required,
		},
	},
	computed: {
		...mapState('profile', {
			permissions: 'permissions',
		}),

		...mapState({
			isLoading: (state) => state.auth.isLoading,
			errorLogin: (state) => state.auth.error,
			errorLoginMessage: (state) => state.auth.errorMessage,
			nextLoginAllowedAt: (state) => state.auth.nextLoginAllowedAt,
		}),

		...mapGetters({
			getDefaultRouteName: 'profile/getDefaultRouteName',
		}),

		errorUsername() {
			return this.errorLogin || pathOr(false, ['$v', 'username', '$error'])(this);
		},
		errorUsernameText() {
			const validateRequired = pathOr(false, ['$v', 'username', 'required'])(this);

			if (!validateRequired) {
				return this.$t(errorMessage.REQUIRED_FIELD);
			}
			return null;
		},
		isUsernameValid() {
			// If error return false, if not return null
			return this.errorUsername ? false : null;
		},
		errorPassword() {
			return this.errorLogin || pathOr(false, ['$v', 'password', '$error'])(this);
		},
		errorPasswordText() {
			const validateRequired = pathOr(false, ['$v', 'password', 'required'])(this);

			if (!validateRequired) {
				return this.$t(errorMessage.REQUIRED_FIELD);
			} else if (this.errorLogin) {
				return this.$t(this.errorLoginMessage);
			}
			return null;
		},
		isPasswordValid() {
			// If error return false, if not return null
			return this.errorPassword ? false : null;
		},
		showLoginForm() {
			return process.env.VUE_APP_IS_SHOW_LOGIN_FORM_ENABLED === '1';
		},
	},

	mounted() {
		document.body.style.setProperty('--version', `"v${this.version}"`);
	},

	beforeDestroy() {
		// Clear timer when component is destroyed
		if (this.allowLoginTimer) {
			clearInterval(this.allowLoginTimer);
		}
	},

	methods: {
		...mapActions({
			postLogin: 'auth/postLogin',
			clearLoginError: 'auth/clearError',
			getProfile: 'profile/getProfile',
		}),

		startAllowLoginTimer() {
			// initial value
			const now = Math.floor(Date.now() / 1000);
			const seconds = this.nextLoginAllowedAt - now;
			this.allowLoginSeconds = seconds;
			this.allowLoginMessage = this.$t('page.login.form.button.submitCoutdown', {
				seconds,
			});

			this.setupAllowLoginTimer();
		},

		setupAllowLoginTimer() {
			// Clear exist timer
			if (this.allowLoginTimer) {
				clearInterval(this.allowLoginTimer);
				this.allowLoginTimer = null;
			}

			this.allowLoginTimer = setInterval(() => {
				const now = Math.floor(Date.now() / 1000);
				const seconds = this.nextLoginAllowedAt - now;
				if (seconds > 0) {
					this.allowLoginSeconds = seconds;
					this.allowLoginMessage = this.$t('page.login.form.button.submitCoutdown', {
						seconds,
					});
				} else {
					this.allowLoginSeconds = 0;
					clearInterval(this.allowLoginTimer);
					this.allowLoginTimer = null;
				}
			}, 1000);
		},

		async handleLogin() {
			this.$v.$touch();

			if (!this.$v.$invalid) {
				try {
					await this.postLogin({
						username: this.username,
						password: this.password,
					});

					if (!this.errorLogin) {
						await this.getProfile();

						const routeName = this.getDefaultRouteName;
						this.$router.push({ name: routeName });
					} else if (this.nextLoginAllowedAt) {
						this.startAllowLoginTimer();
					}
				} catch (error) {
					// TODO: handle error
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.logo {
	width: 88px;
	height: 88px;
	margin: 0 auto 24px;
	background-color: #eaeaea;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;

	.icon {
		font-size: rem(40);
		color: #fff;
		line-height: 1;
		margin: 0;
		text-align: center;
	}
}

.login-env-warning {
	position: fixed;
	top: 20px;
	left: 20px;
	z-index: 9999;

	// .login-env-warning__box
	&__box {
		max-width: 300px;
		font-size: 13px;
		background-color: #fff3cd;
		border: 1px solid #ffc107;
		border-radius: 8px;
		padding: 12px 14px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

		code {
			display: inline-block;
			font-size: 11px;
			background-color: rgba(0, 0, 0, 0.08);
			padding: 1px 5px;
			border-radius: 3px;
		}
	}

	// .login-env-warning__header
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;
	}

	// .login-env-warning__toggle
	&__toggle {
		background: none;
		border: none;
		cursor: pointer;
		font-size: 14px;
		line-height: 1;
		color: #856404;
		padding: 0 0 0 8px;

		&:hover {
			opacity: 0.7;
		}
	}

	// .login-env-warning__badge
	&__badge {
		background-color: #ffc107;
		border: none;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		font-size: 18px;
		cursor: pointer;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			background-color: #ffca2c;
		}
	}
}

.login-form {
	background-color: #fff;
	padding: 24px;
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
	max-width: 360px;
	margin: 0 auto;

	// .login-form-heading
	&-heading {
		font-size: 20px;
		text-align: center;
	}

	// .login-form-description
	&-description {
		font-size: 14px;
		text-align: center;
	}


	// .login-form-input
	&-input {
		margin-bottom: 20px;

		// overwrite hide append icon
		::v-deep .is-invalid {
			background-image: none;
		}
	}

	&::after {
		content: var(--version);
		position: fixed;
		bottom: 20px;
		right: 20px;
		opacity: 0.3;
		font-size: rem(14);
		color: #000;
		pointer-events: none;
		z-index: 9999;
		user-select: none;
		white-space: nowrap;
	}
}

.submit-button {
	min-width: 100%;
}
</style>
